/**
 * Owner: Masa Bayu
 * File: edit-justify-group-paint.ts
 * Date:Time: 2026-10-13 23:56
 * Description: Paint groups · tip textAlign wrap join (R7 · decide retired).
 */

import type { UniversalAtom } from '../types';

/** Same band as Face X_ALIGN_TOL · paint projection only. */
export const EDIT_JUSTIFY_X_TOL_PT = 2;
/** Line-pitch window · matches Face LINE_HEIGHT_MIN/MAX. */
export const EDIT_JUSTIFY_LINE_HEIGHT_MIN = 1.15;
export const EDIT_JUSTIFY_LINE_HEIGHT_MAX = 1.85;
/**
 * First-line indent of a new paragraph · must exceed wrap noise but stay
 * below dual-column gutter separation on witnessed books.
 */
export const EDIT_JUSTIFY_NEW_PARA_INDENT_PT = 18;

export type EditJustifyGroupRole = 'middle' | 'final';

function emSize(atom: UniversalAtom): number {
  const size = atom.font?.size;
  if (typeof size === 'number' && size > 0) return size;
  return atom.bbox.height > 0 ? atom.bbox.height : 11;
}

function sameX(a: UniversalAtom, b: UniversalAtom): boolean {
  return Math.abs(a.bbox.x - b.bbox.x) <= EDIT_JUSTIFY_X_TOL_PT;
}

function sameFont(a: UniversalAtom, b: UniversalAtom): boolean {
  const fa = a.font?.size ?? 0;
  const fb = b.font?.size ?? 0;
  return Math.abs(fa - fb) <= 0.5;
}

function consistentLineHeight(prev: UniversalAtom, next: UniversalAtom): boolean {
  const dy = next.bbox.y - prev.bbox.y;
  if (!(dy > 0)) return false;
  const em = emSize(prev);
  const ratio = dy / em;
  return (
    ratio >= EDIT_JUSTIFY_LINE_HEIGHT_MIN &&
    ratio <= EDIT_JUSTIFY_LINE_HEIGHT_MAX
  );
}

/**
 * Tip textAlign === justify · authorizes wrap-continuation joins only.
 * Not invent (R7) · L3 tip SoT.
 */
function tipAlignAllowsWrapContinue(atom: UniversalAtom): boolean {
  return atom.textAlign === 'justify';
}

/**
 * Dual-column guard · when left edges span more than ~45% of column width,
 * partition by midpoint gutter so left/right bands never join.
 */
export function editJustifyColumnId(
  x: number,
  peers: readonly UniversalAtom[],
  bodyLeft: number,
  columnRight: number,
): number {
  if (peers.length === 0) return 0;
  let minX = peers[0]!.bbox.x;
  let maxX = peers[0]!.bbox.x;
  for (const p of peers) {
    if (p.bbox.x < minX) minX = p.bbox.x;
    if (p.bbox.x > maxX) maxX = p.bbox.x;
  }
  const columnWidth = Math.max(1, columnRight - bodyLeft);
  if (maxX - minX <= columnWidth * 0.45) return 0;
  const gutter = (minX + maxX) / 2;
  return x < gutter ? 0 : 1;
}

function canAppendPaintLine(
  prev: UniversalAtom,
  next: UniversalAtom,
  columnLeft: number,
  columnRight: number,
): boolean {
  if (!sameFont(prev, next) || !consistentLineHeight(prev, next)) return false;
  if (sameX(prev, next)) return true;
  /*
   * Wrap after first-line indent / dropcap host → column left.
   * P02-FIX-01: only when prev tip-fills the page column. Centered imprint
   * stacks (x drifts left as lines lengthen) must NOT join via this branch.
   */
  if (prev.bbox.x > next.bbox.x + EDIT_JUSTIFY_X_TOL_PT) {
    void columnRight;
    void columnLeft;
    return tipAlignAllowsWrapContinue(prev);
  }
  // New paragraph · next is first-line indent relative to column / prev.
  if (
    next.bbox.x >= columnLeft + EDIT_JUSTIFY_NEW_PARA_INDENT_PT &&
    next.bbox.x > prev.bbox.x + EDIT_JUSTIFY_X_TOL_PT
  ) {
    return false;
  }
  return false;
}

export type EditJustifyGroupPaint = {
  readonly roles: ReadonlyMap<string, EditJustifyGroupRole>;
  /**
   * Max tip right (x+w) within each paint group only.
   * Prevents page-global columnRight from stretching short imprint/credit
   * groups that share a page with a long justified paragraph.
   */
  readonly paintRightById: ReadonlyMap<string, number>;
};

function commitGroup(
  group: readonly UniversalAtom[],
  roles: Map<string, EditJustifyGroupRole>,
  paintRightById: Map<string, number>,
): void {
  if (group.length === 0) return;
  let gRight = group[0]!.bbox.x + group[0]!.bbox.width;
  for (let i = 1; i < group.length; i += 1) {
    const r = group[i]!.bbox.x + group[i]!.bbox.width;
    if (r > gRight) gRight = r;
  }
  for (let i = 0; i < group.length; i += 1) {
    const atom = group[i]!;
    roles.set(atom.id, i === group.length - 1 ? 'final' : 'middle');
    paintRightById.set(atom.id, gRight);
  }
}

/**
 * Build paint-only middle/final roles + group-local paint rights.
 * Does not mutate tip · not QXW/SPSU semantic authority.
 * No MIN_BODY_WIDTH gate (Face grouper severed short finals at 160pt).
 */
export function buildEditJustifyGroupPaint(
  peers: readonly UniversalAtom[],
  bodyLeft: number,
  columnRight: number,
): EditJustifyGroupPaint {
  const roles = new Map<string, EditJustifyGroupRole>();
  const paintRightById = new Map<string, number>();
  if (peers.length === 0) return { roles, paintRightById };

  const sorted = [...peers].sort(
    (a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x,
  );

  const byColumn = new Map<number, UniversalAtom[]>();
  for (const atom of sorted) {
    const cid = editJustifyColumnId(
      atom.bbox.x,
      sorted,
      bodyLeft,
      columnRight,
    );
    const bucket = byColumn.get(cid);
    if (bucket) bucket.push(atom);
    else byColumn.set(cid, [atom]);
  }

  for (const columnPeers of byColumn.values()) {
    const columnLeft = columnPeers.reduce(
      (min, a) => Math.min(min, a.bbox.x),
      columnPeers[0]!.bbox.x,
    );
    let group: UniversalAtom[] = [];
    for (const atom of columnPeers) {
      const prev = group[group.length - 1];
      if (prev && canAppendPaintLine(prev, atom, columnLeft, columnRight)) {
        group.push(atom);
        continue;
      }
      commitGroup(group, roles, paintRightById);
      group = [atom];
    }
    commitGroup(group, roles, paintRightById);
  }

  return { roles, paintRightById };
}

/** Roles-only view · same groups as {@link buildEditJustifyGroupPaint}. */
export function buildEditJustifyGroupRoles(
  peers: readonly UniversalAtom[],
  bodyLeft: number,
  columnRight: number,
): ReadonlyMap<string, EditJustifyGroupRole> {
  return buildEditJustifyGroupPaint(peers, bodyLeft, columnRight).roles;
}
